import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { scrollEdges } from './flow'
import './flow.css'

// A region that scrolls inside the page (release 3.3, task F2 of the 7 Oct
// plan, rule 2.1).
//
// # WHY THIS EXISTS
//
// A table or a list taller than the window made the whole page scroll, so the
// page's title, its filters and its one main action went off the top while
// the reader worked down the rows. In a scroll panel the rows scroll and the
// rest of the page stays; a table's header row stays at the top of the panel
// (the panel is the scroll container, so `position: sticky` on a th sticks to
// it).
//
// # WHAT IT PROMISES
//
//  - An edge shadow where there is more to see past that edge, and none where
//    there is not (flow.ts, scrollEdges), so the reader can tell a panel that
//    has more from one that has ended.
//  - A keyboard user can scroll it: it is a named region that takes focus
//    while there is more to see, so the arrow keys and Page Down move it
//    (WCAG 2.1.1); with nothing to scroll it is no tab stop. Name it with
//    `label`.
//  - resetKey: change it (the page, the row size) and the panel goes back to
//    its top.
//  - At most two on a page and never one inside another (rule 2.1); the panel
//    cannot enforce that, the budget test does.

export function ScrollPanel({
  label,
  children,
  maxHeight = '60vh',
  className,
  resetKey,
}: {
  label: string
  children: ReactNode
  // Changing it scrolls the panel back to its top: the page or the row size a
  // Pager under the panel shows. Without it the next page opens part-way down
  // and its first rows are out of view (review of 12 Oct, S2).
  resetKey?: string | number
  // Any CSS length. 60% of the window by default, which leaves the page's
  // head and a footer in view on a 720 px laptop.
  maxHeight?: string
  className?: string
}) {
  const box = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ top: false, bottom: false })

  useEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => {
      const next = scrollEdges(el.scrollTop, el.scrollHeight, el.clientHeight)
      setEdges((was) => (was.top === next.top && was.bottom === next.bottom ? was : next))
    }
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    // The content can grow after the first draw (rows arriving), and the panel
    // can shrink with the window: either changes whether there is more.
    const seen = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null
    seen?.observe(el)
    if (el.firstElementChild) seen?.observe(el.firstElementChild)
    return () => {
      el.removeEventListener('scroll', measure)
      seen?.disconnect()
    }
  }, [])

  useEffect(() => {
    const el = box.current
    if (el && el.scrollTop !== 0) el.scrollTop = 0
  }, [resetKey])

  const style: CSSProperties = { maxHeight }
  return (
    <div
      ref={box}
      className={'kit-scroll' + (className ? ' ' + className : '')}
      role="region"
      aria-label={label}
      // A tab stop only while there is something to scroll to with the keys.
      tabIndex={edges.top || edges.bottom ? 0 : undefined}
      style={style}
      data-edge-top={edges.top ? '' : undefined}
      data-edge-bottom={edges.bottom ? '' : undefined}
    >
      {children}
    </div>
  )
}
